'use client';

import { useIsomorphicLayoutEffect } from '@magicschool/spellbook/hooks/useIsomorphicLayoutEffect';
import { usePrefersReducedMotion } from '@magicschool/spellbook/hooks/usePrefersReducedMotion';
import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import { useEffect, useRef, useState } from 'react';
import type { AnimatedIconHandle, AnimatedIconName } from './constants';
import { ANIMATED_ICON_NAMES, ICON_STROKE_WIDTH } from './constants';

// Typed predicate avoids the `name as AnimatedIconName` cast downstream.
const ANIMATED_ICON_NAMES_SET: ReadonlySet<string> = new Set(ANIMATED_ICON_NAMES);
function isAnimatedIconName(name: string): name is AnimatedIconName {
  return ANIMATED_ICON_NAMES_SET.has(name);
}

// Explicit allowlist of props the animated component's <div> root accepts.
// data-*, aria-*, and on* handlers are matched by prefix below.
const DIV_PASSTHROUGH_ATTRS = new Set(['id', 'role', 'tabIndex', 'title', 'style', 'className']);

let animatedModule: typeof import('./animated/map') | null = null;

function preloadAnimatedMap(): Promise<void> {
  if (animatedModule) return Promise.resolve();
  return import('./animated/map').then((m) => {
    animatedModule = m;
  });
}

// Props passed by Icon after it resolves defaults and pre-computes resolvedClass.
// Typed against React.ComponentProps<'svg'> (minus name/role which we define explicitly)
// so the remaining SVG/HTML pass-through props flow through correctly.
// Named distinctly from the animated components' own AnimatedIconProps (hooks/useIconAnimation.ts),
// which is div-based — this one is the svg-based contract Icon delegates with.
type AnimatedIconDelegateProps = Omit<React.ComponentProps<'svg'>, 'name' | 'role'> & {
  animate: 'mount' | 'hover';
  animating?: boolean;
  name: IconName;
  role: string;
  resolvedClass: string;
};

export const AnimatedIcon: React.FC<AnimatedIconDelegateProps> = ({ animate, animating, name, role, resolvedClass, ...props }) => {
  // usePrefersReducedMotion initializes synchronously from matchMedia (its useState lazy
  // initializer runs getPrefersReducedMotion directly), so prefersReducedMotion is correct on
  // the first render — including warm-cache mounts where animatedReady initializes true and the
  // static-fallback guard would otherwise be bypassed for reduced-motion users.
  const prefersReducedMotion = usePrefersReducedMotion();
  const [animatedReady, setAnimatedReady] = useState(() => animatedModule !== null);
  const animRef = useRef<AnimatedIconHandle>(null);
  // Tracks the last handle seen — prevents stopAnimation() on a freshly-mounted ref that
  // was never started, which would hide the icon (motion quirk with initial state).
  const lastHandleRef = useRef<AnimatedIconHandle | null>(null);

  useEffect(() => {
    if (animatedReady || prefersReducedMotion) return;
    preloadAnimatedMap()
      .then(() => setAnimatedReady(true))
      .catch(console.error);
  }, [animatedReady, prefersReducedMotion]);

  useIsomorphicLayoutEffect(() => {
    if (!animRef.current || !animatedReady) return;
    if (animate === 'mount') {
      animRef.current.startAnimation();
    }
  }, [animate, animatedReady]);

  useIsomorphicLayoutEffect(() => {
    if (!animRef.current || !animatedReady || animate !== 'hover' || animating === undefined) return;
    const isFreshMount = animRef.current !== lastHandleRef.current;
    lastHandleRef.current = animRef.current;
    if (animating) {
      animRef.current.startAnimation();
    } else if (!isFreshMount) {
      animRef.current.stopAnimation();
    }
  }, [animate, animating, animatedReady]);

  const staticIcon = <DynamicIcon className={resolvedClass} name={name} role={role} strokeWidth={ICON_STROKE_WIDTH} {...props} />;

  if (prefersReducedMotion || !animatedReady || !animatedModule) {
    return staticIcon;
  }

  if (!isAnimatedIconName(name)) {
    console.warn(`[Icon] "${name}" has no animated variant. Falling back to static icon.`);
    return staticIcon;
  }

  // ANIMATED_ICON_MAP uses `satisfies Record<AnimatedIconName, unknown>` which guarantees every
  // AnimatedIconName key has a corresponding entry — no runtime undefined check needed.
  const AnimatedComponent = animatedModule.ANIMATED_ICON_MAP[name];

  const htmlPassthrough = Object.fromEntries(
    Object.entries(props).filter(
      ([k]) => DIV_PASSTHROUGH_ATTRS.has(k) || k.startsWith('data-') || k.startsWith('aria-') || k.startsWith('on'),
    ),
  ) as React.HTMLAttributes<HTMLDivElement>;

  // Attach ref for imperative control: 'mount' triggers startAnimation on mount;
  // 'hover' with animating drives start/stop from the parent. Without a ref,
  // the icon component's built-in animateOnHover handles the hover case autonomously.
  const useImperativeRef = animate === 'mount' || animating !== undefined;

  return (
    <AnimatedComponent
      ref={useImperativeRef ? animRef : undefined}
      animateOnHover={animate === 'hover' && animating === undefined}
      className={resolvedClass}
      role={role}
      {...htmlPassthrough}
    />
  );
};
